import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CreditCard } from "lucide-react";

const money = (c, cur = "CAD") => `$${((c || 0) / 100).toFixed(0)} ${cur}`;

export default function WwbaBalance() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(!!sessionId);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wwba/balance/${token}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "This payment link is invalid.");
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setErr("Missing payment token."); return; }
    if (canceled) toast.info("Payment canceled — your balance is still due. You can pay anytime.");
    if (sessionId) {
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/wwba/balance/${token}/status/${sessionId}`);
          if (data.paid_in_full) { setData((d) => ({ ...(d || {}), paid_in_full: true, pricing: { ...(d?.pricing || {}), balance_cents: 0, paid_cents: data.total_paid_cents, total_cents: data.total_cost_cents, currency: data.currency } })); setConfirming(false); return; }
        } catch { /* ignore */ }
        if (++tries < 8) setTimeout(poll, 1500); else { setConfirming(false); load(); }
      };
      poll();
    } else {
      load();
    }
  }, [token, sessionId, canceled, load]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wwba/balance/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  if (err) return <Shell><div data-testid="wwba-balance-error" className="text-center py-20 text-slate-600">{err}</div></Shell>;
  if (confirming) return <Shell><div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#E63946]" /><p className="mt-3 text-slate-500">Confirming your payment…</p></div></Shell>;
  if (!data) return <Shell><div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></Shell>;

  const p = data.pricing || {};
  if (data.paid_in_full) return <PaidInFull data={data} />;

  return (
    <Shell event={data.event}>
      <div className="max-w-xl mx-auto px-4 pb-20" data-testid="wwba-balance-page">
        <div className="bg-white border border-slate-200 rounded-sm p-6 text-center">
          <div className="label-overline text-[#E63946]">Final Balance Due</div>
          <h2 className="font-display text-2xl uppercase tracking-tight mt-1">{data.player_name}</h2>
          {data.event && <p className="text-sm text-slate-500 mt-1">{data.event.name}</p>}
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div><div className="text-[11px] uppercase tracking-wider text-slate-400">Total</div><div className="font-data text-xl text-[#0A192F]">{money(p.total_cents, p.currency)}</div></div>
            <div className="border-x border-slate-100"><div className="text-[11px] uppercase tracking-wider text-slate-400">Already Paid</div><div className="font-data text-xl text-emerald-600">{money(p.paid_cents, p.currency)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-slate-400">Balance Due</div><div className="font-data text-xl text-[#E63946]">{money(p.balance_cents, p.currency)}</div></div>
          </div>
          <button data-testid="wwba-balance-pay" onClick={pay} disabled={busy} className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#E63946] text-white font-semibold uppercase tracking-wider text-sm py-3 rounded-sm disabled:opacity-50">
            <CreditCard className="w-4 h-4" /> {busy ? "Please wait…" : `Pay ${money(p.balance_cents, p.currency)} Balance`}
          </button>
          <p className="text-[11px] text-slate-400 mt-3">Secure payment via Stripe. This completes your player's WWBA Florida registration.</p>
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children, event }) => (
  <div className="min-h-screen bg-slate-50">
    <div className="bg-[#0A192F] text-white py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="label-overline text-[#E63946]">CBR Nation</div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-1">WWBA Final Balance</h1>
        {event && <p className="text-sm text-white/60 mt-1">{[event.location, (event.start_date && event.end_date) ? `${event.start_date} – ${event.end_date}` : null].filter(Boolean).join(" · ")}</p>}
      </div>
    </div>
    <div className="pt-8">{children}</div>
  </div>
);

function PaidInFull({ data }) {
  const p = data.pricing || {};
  return (
    <Shell event={data.event}>
      <div className="max-w-xl mx-auto px-4 text-center pb-20" data-testid="wwba-balance-paid">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
        <h2 className="font-display text-3xl uppercase tracking-tight mt-4">Paid in Full</h2>
        <p className="text-slate-500 mt-1">{data.player_name} — CBR Nation WWBA Florida</p>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="text-[11px] uppercase tracking-wider text-slate-400">Total Paid</div><div className="font-data text-2xl text-emerald-600">{money(p.paid_cents ?? p.total_cents, p.currency)}</div></div>
          <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="text-[11px] uppercase tracking-wider text-slate-400">Balance</div><div className="font-data text-2xl text-slate-600">$0 {p.currency || "CAD"}</div></div>
        </div>
        <p className="text-sm text-slate-500 mt-6">Your player's registration is complete. Additional WWBA trip information will be provided as the event approaches.</p>
      </div>
    </Shell>
  );
}
